/**
 * Os textos do ACESSO DE PLATAFORMA — o Admin ("Admin Fincast" na tela) dentro de uma incorporadora.
 *
 * Sem DOM, para o teste ler a regra direto (`tests/acesso-plataforma.test.ts`). Quem desenha é a tela
 * (Incorporadoras e Empreendimentos).
 *
 * ADM-3 (revisão adversarial de 12/10/2026): desde a 0031 o Admin tem os poderes de gestor só em
 * incorporadora ATIVA (`emp_escrita`/`emp_admin` filtram `ativa`). O texto do D23 — "Pode consultar e
 * editar tudo, com os poderes de gestor" — e o title do selo apareciam também na INATIVA, ao lado do
 * cartão que diz "Inativa — nada pode ser feito nela". Na inativa, o texto diz o que ela é.
 */

/** O aviso do EL-180, para o Admin que entra sem ser da equipe. */
export function avisoDeFora(ativa: boolean): string {
  return ativa
    /* D23 (12/10/2026, aprovado como proposto) */
    ? 'Você está nesta incorporadora como Admin Fincast, sem ser da equipe dela. Pode consultar e editar tudo, '
      + 'com os poderes de gestor — e esta visita fica registrada para a incorporadora.'
    /* ADM-3: o mesmo começo e o mesmo fim do D23; no meio, o que de fato vale na inativa (revisão do
       cético, 12/10/2026 — a 1ª versão dizia "nada pode ser feito nela" e tirava o registro):
       · fechados: empreendimentos e cenários (`emp_leitura`/`emp_escrita` filtram `ativa`) e os membros
         (`membro_sel` passa por `emp_leitura`);
       · aberto: o convite pela Equipe (`convite_*` é regra de plataforma, vale na inativa — 0031, D20);
       · a visita continua registrada (`app.registrar_acesso_admin`, 0017, não olha `ativa`). */
    : 'Você está nesta incorporadora como Admin Fincast, sem ser da equipe dela. Ela está inativa: empreendimentos, '
      + 'cenários e membros ficam fechados até ser restaurada; pela Equipe, ainda dá para convidar — e esta visita '
      + 'fica registrada para a incorporadora.';
}

/** O title do selo "Acesso de plataforma" no cartão da lista de incorporadoras. */
export function tituloDoAcessoDePlataforma(ativa: boolean): string {
  return ativa
    ? 'Você não é da equipe desta incorporadora: entra como Admin Fincast, com os poderes de gestor.'
    : 'Você não é da equipe desta incorporadora: entra como Admin Fincast.';
}

/**
 * A lista de empreendimentos vazia. Na inativa ela vem vazia porque a leitura filtra `ativa`
 * (`emp_leitura`) — os empreendimentos existem; "nenhum cadastrado" seria falso.
 */
export function textoSemEmpreendimentos(o: { editavel: boolean; ativa: boolean }): string {
  if (!o.ativa) return 'Os empreendimentos de uma incorporadora inativa não aparecem aqui.';
  return o.editavel ? 'Nenhum empreendimento por aqui ainda.' : 'Nenhum empreendimento cadastrado nesta incorporadora.';
}

/**
 * ADM-4 (revisão adversarial de 12/10/2026): o selo do papel de quem é MEMBRO. O Admin com vínculo
 * de QUALQUER papel aparece como "Admin Fincast" — desde a 0031 o que vale para ele são os poderes de
 * plataforma, e o selo "Consulta" ao lado do menu de gestor era contraditório. O papel do vínculo
 * continua dito, no title. (O aviso EL-180 e o registro de acesso NÃO valem para o Admin membro: a
 * 0017 trata membro como acesso comum — `acessoDePlataforma`.)
 */
export function seloDoMembro(nomeDoPapel: string, souAdmin: boolean): { texto: string; titulo: string | null } {
  return souAdmin
    ? { texto: 'Admin Fincast', titulo: `Papel na equipe: ${nomeDoPapel}.` }
    : { texto: nomeDoPapel, titulo: null };
}
